/**
 * Deferring work until it is worth doing, and the three media queries that decide
 * whether it is worth doing at all.
 *
 * Lifted out of the old hand-coded homepage (`apps/web_site/src/home/motion.ts`,
 * removed 2026-10-02), which is where these were first written. They are not
 * homepage-specific: any widget with a heavy engine wants exactly this pair of gates,
 * and a widget in `packages/widgets` cannot import from an app.
 *
 * Every function here is SSR-safe: on the server there is no `window`, and each one
 * answers conservatively (no reduced motion, no coarse pointer, nothing to cancel)
 * rather than throwing.
 */

/** Current reduced-motion preference (SSR-safe: false on the server). */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Subscribe to reduced-motion changes; returns an unsubscribe. Views switch to their
 * static form when this flips on (repo convention: never hide content).
 */
export function onReducedMotionChange(cb: (reduced: boolean) => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
  const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
  const handler = (e: MediaQueryListEvent): void => cb(e.matches);
  mq.addEventListener("change", handler);
  return () => mq.removeEventListener("change", handler);
}

/** Coarse-pointer (touch) check - cursor-driven effects switch to ambient/static. */
export function isCoarsePointer(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(pointer: coarse)").matches;
}

/**
 * Run `init` when the browser is idle (post-LCP) AND `el` is near the viewport.
 * Used to defer heavy engines (a three.js scene) so the SSR text stays the LCP.
 * Returns a cancel function; `init` receives nothing and handles its own cleanup.
 */
export function whenIdleAndVisible(el: Element, init: () => void, rootMargin = "600px"): () => void {
  let cancelled = false;
  let idleId: number | null = null;
  const io = new IntersectionObserver(
    (entries) => {
      // Read the LAST entry - a fast scroll can batch enter+leave together.
      const last = entries[entries.length - 1];
      if (!last?.isIntersecting || cancelled) return;
      io.disconnect();
      const ric: (cb: () => void) => number =
        typeof window.requestIdleCallback === "function"
          ? (cb) => window.requestIdleCallback(cb, { timeout: 1500 })
          : (cb) => window.setTimeout(cb, 200);
      idleId = ric(() => {
        if (!cancelled) init();
      });
    },
    { rootMargin },
  );
  io.observe(el);
  return () => {
    cancelled = true;
    io.disconnect();
    if (idleId !== null && typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idleId);
  };
}

/**
 * Is `el` on screen right now? Calls back on every change until the returned function
 * runs. A widget that animates has to stop when nobody is looking at it: a rAF loop on
 * an off-screen sphere is a phone battery being spent on nothing.
 */
export function onVisibilityChange(el: Element, cb: (visible: boolean) => void): () => void {
  if (typeof IntersectionObserver !== "function") {
    // No observer: assume visible rather than assume hidden. A widget that never
    // starts is a worse failure than one that runs while scrolled past.
    cb(true);
    return () => {};
  }
  const io = new IntersectionObserver((entries) => {
    // The LAST entry, never `entries[0]`: a fast scroll batches enter and leave into
    // one callback, and reading the first one leaves the loop running off screen.
    const last = entries[entries.length - 1];
    if (last) cb(last.isIntersecting);
  });
  io.observe(el);
  return () => io.disconnect();
}
